/**
 * Sign-in links that sign in whichever browser opens them, delivered in place of Clerk's own link.
 *
 * WHY
 *
 * Clerk's email link signs in the browser that ASKED for the link, not the one that opens it. That
 * one rule caused every login complaint: a link opened in another browser says "you're signed in"
 * and isn't; a closed tab leaves nothing to finish the sign-in; the waiting tab gives up after about
 * three minutes; mail scanners spend the link on a plain page load; and whoever requested a link for
 * someone else's address got that person's session when they clicked it.
 *
 * HOW
 *
 *   Clerk's <SignIn/> on `/` (unchanged)   person types their email; Clerk starts a sign-in
 *        -> Clerk "magic link sign-in" email, with "Delivered by Clerk" turned off
 *        -> webhook email.created          server/services/postClerkWebhookForPublic.ts
 *        -> server/util/clerkSignInEmailDelivery.ts
 *             advisor or client, flag on:  mint a Clerk sign-in token, email THIS link
 *             anyone else:                 deliver Clerk's original email unchanged
 *   emailed link -> /auth/link?purpose=sign-in#__clerk_ticket=…
 *        -> a click on "Continue securely" redeems the token on that page, from memory; an account
 *           with two-factor finishes on Clerk's <SignIn/> at `#/factor-two`, without the token
 *
 * Clerk still rate-limits requests, because every request starts in its own card. The token is a
 * Clerk sign-in token, so Clerk enforces expiry and single use. It sits in the URL fragment, so it
 * never reaches a server log; the page takes it out of the address bar on load and never puts it
 * back into a URL, so history, session replay and analytics do not hold it; and redeeming needs a
 * click, so mail scanners cannot spend it. Staff
 * (`wizard`) accounts never get one: internal accounts stopped receiving sign-in tokens on
 * 15 Sep 2026 (see server/util/createAdvisorEmailVerifyLink.ts), so they keep Clerk's link.
 *
 * Gated by the `clerkTokenSignInLink` AppConfig flag, resolved per account when the email is sent.
 */

import { AUTO_SIGN_IN_EXPIRY_SECONDS, clientAutoSignInLink } from './clientAutoSignInLink';

export type SignInLinkRole = 'advisor' | 'client';

/** Marks a link as a sign-in from the sign-in page, so the redeem page words itself for that. */
export const SIGN_IN_LINK_PURPOSE = 'sign-in';

/** Short for advisors, whose accounts reach many clients' data. */
export const ADVISOR_SIGN_IN_LINK_EXPIRY_SECONDS = 15 * 60;
/** Matches the client auto sign-in link that clients already receive. */
export const CLIENT_SIGN_IN_LINK_EXPIRY_SECONDS = AUTO_SIGN_IN_EXPIRY_SECONDS;

export function signInLinkExpirySeconds(role: SignInLinkRole): number {
  return role === 'advisor'
    ? ADVISOR_SIGN_IN_LINK_EXPIRY_SECONDS
    : CLIENT_SIGN_IN_LINK_EXPIRY_SECONDS;
}

export function signInLink({
  ticket,
  redirectTo,
  email,
}: {
  ticket: string;
  redirectTo: string;
  email: string;
}): string {
  return clientAutoSignInLink({ ticket, redirectTo, email, purpose: SIGN_IN_LINK_PURPOSE });
}
